import React, { memo, useEffect, useRef, useState } from 'react';
import IconWrapper from '../../IconWrapper';
import { FiFilter, FiSearch, FiRefreshCw, FiSettings } from 'react-icons/fi';
import DatasetFilter from './feed/DatasetFilter';

type SignalsControlsBarProps = {
  onOpenFilters: () => void;
  hasActiveFilters: boolean;
  searchTerm: string;
  onSearchTermChange: (value: string) => void;
  selectedSize: number;
  deleting: boolean;
  onDeleteSelected: () => void;
  onReclassifySelected?: () => void;
  searchInputRef: React.RefObject<HTMLInputElement | null>;
  searchFocused: boolean;
  setSearchFocused: (focused: boolean) => void;
  visibleColumns?: Set<string>;
  onToggleColumn?: (columnName: string) => void;
  datasetIds: string[];
  onDatasetChange: (datasetIds: string[]) => void;
};

const SignalsControlsBarInner: React.FC<SignalsControlsBarProps> = ({
  onOpenFilters,
  hasActiveFilters,
  searchTerm,
  onSearchTermChange,
  selectedSize,
  deleting,
  onDeleteSelected,
  onReclassifySelected,
  searchInputRef,
  searchFocused,
  setSearchFocused,
  visibleColumns,
  onToggleColumn,
  datasetIds,
  onDatasetChange,
}) => {
  const showSelectionButtons = selectedSize > 0;
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const columnMenuRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (columnMenuRef.current && !columnMenuRef.current.contains(event.target as Node)) {
        setShowColumnSettings(false);
      }
    };

    if (showColumnSettings) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showColumnSettings]);

  return (
    <div data-testid="signals-controls-bar" className="pb-4 space-y-4">
      {/* Main controls row */}
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0 flex items-center gap-3">
        </div>

        <div className="flex items-center gap-3 flex-shrink-0">

          {showSelectionButtons && (
            <>
              {onReclassifySelected && (
                <button
                  onClick={onReclassifySelected}
                  className="inline-flex items-center gap-2 px-3 h-[35px] rounded-[20px] border border-[#4342FF] text-figma-sm text-[#4342FF] hover:bg-[#EEF2FF]"
                >
                  <IconWrapper icon={FiRefreshCw} className="w-4 h-4" />
                  Reclassify ({selectedSize})
                </button>
              )}
              <button
                onClick={onDeleteSelected}
                disabled={deleting}
                className="inline-flex items-center gap-2 px-3 h-[35px] rounded-[20px] border border-red-300 text-figma-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
              >
                {deleting ? 'Deleting…' : `Delete (${selectedSize})`}
              </button>
            </>
          )}

          <button
            onClick={onOpenFilters}
            className="inline-flex items-center justify-center w-[35px] h-[35px] rounded-[20px] border border-figma-border text-figma-sm text-figma-text-secondary hover:bg-gray-50 relative overflow-visible"
            aria-label="Filter"
          >
            <IconWrapper icon={FiFilter} className="w-4 h-4" />
            {hasActiveFilters && (
              <div className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-[#4342FF] rounded-full border border-white"></div>
            )}
          </button>

          {/* Column Settings Dropdown */}
          <div className="relative" ref={columnMenuRef}>
            <button
              onClick={() => setShowColumnSettings(!showColumnSettings)}
              className="inline-flex items-center justify-center w-[35px] h-[35px] rounded-[20px] border border-figma-border text-figma-sm text-figma-text-secondary hover:bg-gray-50"
              aria-label="Columns"
            >
              <IconWrapper icon={FiSettings} className="w-4 h-4" />
            </button>
            {showColumnSettings && (
              <div className="absolute top-full right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                <div className="px-3 py-1 text-xs font-semibold text-gray-500 uppercase">Show/Hide Columns</div>
                {[
                  { key: 'severity', label: 'Severity' },
                  { key: 'date', label: 'Date' },
                  { key: 'customer', label: 'Customer' },
                  { key: 'summary', label: 'Summary' },
                  { key: 'taxonomyVersion', label: 'Taxonomy Version' },
                  { key: 'complaint', label: 'Complaint' },
                  { key: 'vulnerability', label: 'Vulnerability' },
                ].map(column => (
                  <label key={column.key} className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={visibleColumns?.has(column.key) ?? true}
                      onChange={() => onToggleColumn?.(column.key)}
                      className="rounded border-gray-300 text-[#4342FF] focus:ring-[#4342FF] mr-2"
                    />
                    <span className="text-sm text-gray-700">{column.label}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className="relative">
            <IconWrapper icon={FiSearch} className="w-4 h-4 text-[#B8B8B9] absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search summaries"
              className={`${searchTerm ? 'pr-[140px]' : 'pr-7'} pl-9 py-2 bg-[#F4F4F4] border-none rounded-[20px] text-[11px] text-[#262628] placeholder-[#B8B8B9] h-[35px] w-[240px] font-semibold focus:outline-none`}
              value={searchTerm}
              onChange={(e) => onSearchTermChange(e.target.value)}
              ref={searchInputRef}
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setSearchFocused(false)}
            />
            {searchTerm && (
              <div className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center gap-1 px-2 h-[24px] rounded-full bg-[#EEF2FF] border border-[#DDE2FF] text-[#4342FF] text-[11px] font-semibold">
                <span className="hidden md:inline">Search:</span>
                <span className="truncate max-w-[90px]">{searchTerm}</span>
                <button
                  onClick={() => onSearchTermChange('')}
                  className="ml-1 text-[#4342FF] hover:text-[#2524CC]"
                  aria-label="Clear search"
                >
                  ✕
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Filter row */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <DatasetFilter
            selectedDatasetIds={datasetIds}
            onDatasetChange={onDatasetChange}
          />
        </div>
      </div>
    </div>
  );
};

const SignalsControlsBar = memo(
  SignalsControlsBarInner,
  (prev, next) => {
    if (prev.searchTerm !== next.searchTerm) return false;
    if (prev.hasActiveFilters !== next.hasActiveFilters) return false;
    if (prev.selectedSize !== next.selectedSize) return false;
    if (prev.deleting !== next.deleting) return false;
    if (prev.datasetIds.join(',') !== next.datasetIds.join(',')) return false;
    return true;
  }
);

export default SignalsControlsBar;
